import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert } from "@material-ui/lab";
import { NavLink } from "react-router-dom";
import { TextField } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { getServiceNameWithNickname } from "../../../../utils/getServiceNameWithNickname";
import DialogModal from "../../../elements/dialogModal/DialogModal";
import TwoFa from "../../../elements/twoFa/TwoFa";
import PageSpinner from "../../../elements/spinner/PageSpinner";
import userAuthenticationConfig from "../../../../utils/userAuthenticationConfig";
import { parseAxiosError } from "../../../../utils/response";
import { HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../../utils/consts";

import DefaultButton from "../../../styles/materialButtonStyle";
import {
  StyledCol,
  StyledScrollTable,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../../styles/styledTable";
import { StyledCheckbox, StyledFormGrid } from "../../../styles/styledTabsForm";
import MaterialCheckbox from "../../../styles/materialCheckbox";
import { StyledApplyToItem, StyledApplyToWrapper } from "../../../styles/styledApplyTo";

const ExchangePairsList = ({ pairs, notification, setNotification, fetchPairs }) => {
  const { t } = useTranslation("exchangerPage");

  const [open, setOpen] = useState(false);
  const [error, setError] = useState({});

  const [checked, setChecked] = useState([]);
  const [massEditingData, setMassEditingData] = useState({
    percent: 0,
    constant: 0
  });

  const inputType = {
    percent: "percent",
    constant: "constant"
  };

  const [currentType, setCurrentType] = useState(null);

  const onChangeApply = (event, index) => {
    setChecked((prev) => {
      const updated = [...prev];
      updated[index] = event.target.checked;
      return updated;
    });
  };

  const handleCheckAll = (event) => {
    setChecked(pairs.map(() => event.target.checked));
  };

  const renderApplyButton = (action) => {
    return (
      <DefaultButton color="success" onClick={action}>
        {t("pairs.table.applyTo")} {checked?.filter(Boolean).length}/{pairs.length}
      </DefaultButton>
    );
  };

  const changeInputValue = (event, type) => {
    setMassEditingData({ ...massEditingData, [type]: event.target.value });
  };

  const massEditing = async (event) => {
    event.preventDefault();

    const twoFa = event.target.twofa.value;

    let data = document.getElementsByName("applyTo"), res = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i].checked) {
        res.push(data[i].id);
      }
    }

    axios.post("/api/exchanger-pairs/mass-edit", {
        data: res,
        type: currentType,
        value: massEditingData[currentType],
        twoFa
      },
      userAuthenticationConfig()
    ).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        fetchPairs();
        setOpen(false);
        setNotification({ ...notification, visible: true, type: "success", message: t("pairs.table.success") });
      }
    }).catch(error => {
      setOpen(false);
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  useEffect(() => {
    setChecked(pairs?.map(() => false) || []);
  }, [pairs]);

  const renderMassEdit = () => {
    return (
      <StyledFormGrid className="apply-to-wrapper">
        <StyledApplyToWrapper col="4">
          <StyledApplyToItem>
            <TextField
              className="grid-form__input"
              variant="filled"
              label={t("pairs.table.percent")}
              id={inputType.percent}
              type="text"
              value={massEditingData.percent}
              onChange={(event => changeInputValue(event, inputType.percent))}
            />
            {renderApplyButton(() => {
              setCurrentType(inputType.percent);
              setOpen(true);
            })}
          </StyledApplyToItem>
          <StyledApplyToItem>
            <TextField
              className="grid-form__input"
              variant="filled"
              label={t("pairs.table.constant")}
              id={inputType.constant}
              type="text"
              value={massEditingData.constant}
              onChange={(event => changeInputValue(event, inputType.constant))}
            />
            {renderApplyButton(() => {
              setCurrentType(inputType.constant);
              setOpen(true);
            })}
          </StyledApplyToItem>
        </StyledApplyToWrapper>
      </StyledFormGrid>
    );
  };

  if (!pairs) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  if (pairs.length === 0) {
    return <Alert severity="warning">{t("pairs.table.emptyData")}</Alert>;
  }

  return (
    <>
      <DialogModal
        open={open}
        setOpen={setOpen}
        title={t("pairs.table.twoFa")}
        content={<TwoFa
          handleSubmit={massEditing}
          error={error}
        />}
      />
      {renderMassEdit()}
      <StyledScrollTable>
        <StyledTable
          width="1280"
          size="14"
        >
          <StyledTableHead
            scroll="auto"
            col="6"
          >
            <div>
              <StyledCheckbox position="center">
                <MaterialCheckbox
                  type="checkbox"
                  name="checkAll"
                  id="checkAll"
                  checked={checked?.length > 0 ? checked.every(Boolean) : false}
                  indeterminate={checked?.length > 0 && checked.some(Boolean) && !checked.every(Boolean)}
                  onChange={handleCheckAll}
                />
              </StyledCheckbox>
            </div>
            <div>
              {t("pairs.table.give")}
            </div>
            <div>
              {t("pairs.table.receive")}
            </div>
            <div>
              {t("pairs.table.percent")}
            </div>
            <div>
              {t("pairs.table.constant")}
            </div>
            <div />
          </StyledTableHead>
          <StyledTableBody>
            {pairs && pairs.map((value, index) => (
              <StyledTableRow
                key={index}
                scroll="auto"
                col="6"
              >
                <StyledCol>
                  <StyledCheckbox position="center">
                    <MaterialCheckbox
                      type="checkbox"
                      name="applyTo"
                      id={value.id}
                      checked={checked?.[index] || false}
                      onChange={(event) => onChangeApply(event, index)}
                    />
                  </StyledCheckbox>
                </StyledCol>
                <StyledCol>
                  {getServiceNameWithNickname(value.inService.name, value.inService.nickname)} {value.inCurrency.asset}
                </StyledCol>
                <StyledCol>
                  {getServiceNameWithNickname(value.outService.name, value.outService.nickname)} {value.outCurrency.asset}
                </StyledCol>
                <StyledCol>
                  {Math.round((value.percent * 100) * 1000) / 1000} %
                </StyledCol>
                <StyledCol>
                  {value.constant}
                </StyledCol>
                <StyledCol inline>
                  <NavLink
                    className="table-action table-action_info"
                    to={"/exchange/pairs/" + value.id}
                  >
                    <span className="icon-light-edit" />
                    {t("pairs.table.edit")}
                  </NavLink>
                </StyledCol>
              </StyledTableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledScrollTable>
    </>
  );
};

export default ExchangePairsList;